import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, filter, forkJoin, take, takeUntil } from 'rxjs';
import { AuthService } from '../../../services/auth.service';
import {
  CapiDeliveryHealthDto,
  ConversionRateBySourceDto,
  EnquiryTrendPointDto,
  MarketingAttributionService,
  TimeToConversionDto,
  UnconvertedLeadDto
} from '../../../services/marketing-attribution.service';
import { ChartWrapperComponent } from '../../charts/shared/chart-wrapper/chart-wrapper.component';
import { EntityChartDataDto } from '../../../models/chart.models';
import { PaginationComponent } from '../../shared/pagination/pagination.component';
import { DateInputComponent } from '../../shared/date-input/date-input.component';
import { SelectInputComponent, SelectOption } from '../../shared/select-input/select-input.component';

@Component({
  selector: 'app-marketing-reports',
  standalone: true,
  imports: [CommonModule, FormsModule, ChartWrapperComponent, PaginationComponent, DateInputComponent, SelectInputComponent],
  templateUrl: './reports.component.html',
  styleUrl: './reports.component.css'
})
export class MarketingReportsComponent implements OnInit, OnDestroy {
  loading = false;
  errorMessage = '';

  conversionRateData: ConversionRateBySourceDto[] = [];
  enquiryTrendData: EnquiryTrendPointDto[] = [];
  timeToConversionData: TimeToConversionDto[] = [];
  capiHealth: CapiDeliveryHealthDto | null = null;

  // Computed once per loadReports() response, not as template getters — Angular re-evaluates
  // template getters on every change-detection cycle (any click, any timer tick), and these do a
  // filter+reduce+map pass over the full dataset, which is wasted work on every unrelated CD tick.
  conversionRateChart: { direct: EntityChartDataDto; ad: EntityChartDataDto } = {
    direct: this.emptyChartData('Direct'),
    ad: this.emptyChartData('Ad')
  };
  enquiryTrendChart: { direct: EntityChartDataDto; ad: EntityChartDataDto } = {
    direct: this.emptyChartData('Direct'),
    ad: this.emptyChartData('Ad')
  };
  directTimeToConvert: TimeToConversionDto | undefined;
  adTimeToConvert: TimeToConversionDto | undefined;

  fromDate = '';
  toDate = '';

  unconvertedLeads: UnconvertedLeadDto[] = [];
  unconvertedTotalCount = 0;
  unconvertedLoading = false;
  unconvertedSource = '';
  readonly sourceOptions: SelectOption[] = [
    { value: '', label: 'All Sources' },
    { value: 'Direct', label: 'Direct' },
    { value: 'Ad', label: 'Ad' }
  ];
  unconvertedCurrentPage = 1;
  unconvertedPageSize = 10;

  private destroy$ = new Subject<void>();

  constructor(
    private marketingAttributionService: MarketingAttributionService,
    private authService: AuthService
  ) { }

  ngOnInit(): void {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    this.toDate = this.formatDateForInput(today);
    this.fromDate = this.formatDateForInput(thirtyDaysAgo);

    this.authService.getMsalInitializedObservable()
      .pipe(
        filter(initialized => initialized),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.loadReports();
        this.loadUnconvertedLeads();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadReports(): void {
    this.loading = true;
    this.errorMessage = '';

    const from = this.fromDate ? new Date(this.fromDate + 'T00:00:00').toISOString() : undefined;
    const to = this.toDate ? new Date(this.toDate + 'T23:59:59').toISOString() : undefined;

    forkJoin({
      conversionRate: this.marketingAttributionService.getConversionRateBySource(from, to),
      enquiryTrend: this.marketingAttributionService.getEnquiryTrend(from, to),
      timeToConversion: this.marketingAttributionService.getTimeToConversion(from, to),
      capiHealth: this.marketingAttributionService.getCapiDeliveryHealth(from, to)
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ conversionRate, enquiryTrend, timeToConversion, capiHealth }) => {
          this.conversionRateData = conversionRate || [];
          this.enquiryTrendData = enquiryTrend || [];
          this.timeToConversionData = timeToConversion || [];
          this.capiHealth = capiHealth || null;

          this.conversionRateChart = {
            direct: this.toChartData(this.conversionRateData, 'Direct', 'month', 'conversionPercentage'),
            ad: this.toChartData(this.conversionRateData, 'Ad', 'month', 'conversionPercentage')
          };
          this.enquiryTrendChart = {
            direct: this.toChartData(this.enquiryTrendData, 'Direct', 'date', 'count'),
            ad: this.toChartData(this.enquiryTrendData, 'Ad', 'date', 'count')
          };
          this.directTimeToConvert = this.timeToConversionData.find(t => t.source === 'Direct');
          this.adTimeToConvert = this.timeToConversionData.find(t => t.source === 'Ad');

          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading marketing reports:', error);
          this.loading = false;
          if (error.status === 401) {
            this.errorMessage = 'Authentication required. Please log in again.';
          } else if (error.status === 403) {
            this.errorMessage = 'Access denied. You do not have permission to view marketing reports.';
          } else {
            this.errorMessage = 'Failed to load marketing reports. Please try again.';
          }
        }
      });
  }

  loadUnconvertedLeads(): void {
    this.unconvertedLoading = true;

    const from = this.fromDate ? new Date(this.fromDate + 'T00:00:00').toISOString() : undefined;
    const to = this.toDate ? new Date(this.toDate + 'T23:59:59').toISOString() : undefined;

    this.marketingAttributionService
      .getUnconvertedLeads(this.unconvertedSource || undefined, from, to, this.unconvertedCurrentPage - 1, this.unconvertedPageSize)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.unconvertedLeads = response.items || [];
          this.unconvertedTotalCount = response.totalCount;
          this.unconvertedLoading = false;
        },
        error: (error) => {
          console.error('Error loading unconverted leads:', error);
          this.unconvertedLoading = false;
          this.unconvertedLeads = [];
          this.unconvertedTotalCount = 0;
        }
      });
  }

  applyFilters(): void {
    this.unconvertedCurrentPage = 1;
    this.loadReports();
    this.loadUnconvertedLeads();
  }

  onUnconvertedSourceChange(): void {
    this.unconvertedCurrentPage = 1;
    this.loadUnconvertedLeads();
  }

  onUnconvertedPageChange(page: number): void {
    this.unconvertedCurrentPage = page;
    this.loadUnconvertedLeads();
  }

  onUnconvertedPageSizeChange(size: number): void {
    this.unconvertedPageSize = size;
    this.unconvertedCurrentPage = 1;
    this.loadUnconvertedLeads();
  }

  get unconvertedTotalPages(): number {
    return Math.ceil(this.unconvertedTotalCount / this.unconvertedPageSize) || 1;
  }

  private emptyChartData(source: string): EntityChartDataDto {
    return { entityName: source, totalCount: 0, dataPoints: [] };
  }

  private toChartData<T extends { source: string }>(
    rows: T[],
    source: string,
    labelKey: keyof T,
    valueKey: keyof T
  ): EntityChartDataDto {
    const filtered = rows.filter(r => r.source === source);
    return {
      entityName: source,
      totalCount: filtered.reduce((sum, r) => sum + (Number(r[valueKey]) || 0), 0),
      dataPoints: filtered.map(r => ({
        label: String(r[labelKey]),
        value: Number(r[valueKey]) || 0
      }))
    };
  }

  private formatDateForInput(date: Date): string {
    return date.toISOString().substring(0, 10);
  }
}
